import React, { useEffect, useState } from 'react';
import classNames from 'classnames/bind';
import styles from './ImageSlider.pcss';
import properties from '../../../modules/ajio-util/properties';
import { gtmEventPush } from '../../../modules/ga/GTMdatalayer';
import { replaceWithJioCDN } from '../../../utils/imageHandling';

interface ProductDetails {
  brandName: string;
  description: string;
  imageUrl: string;
  productUrl: string;
  productCode: string;
  skuId: string;
  orderId: string;
  brickCode: string;
}

interface ImageSlider {
  imageArray: string[];
  currentIndex: number;
  updateSelectedImageIndex: (currentIndex: number) => void;
  backwardButtonClick?: (val: boolean) => void;
  isMobile: boolean;
  isRejected?: boolean;
  reason?: string;
  displayCountTag?: boolean;
  productDetails?: ProductDetails;
  pageName: string;
  pageType: string;
  fromCustomerGridModal?: boolean;
  isFleek?: boolean;
}
const cx = classNames.bind(styles);
const imageBaseRef = `${properties.getProperty('imageBaseUrl')}/static/img/`;
const ImageSlider = (props: ImageSlider): JSX.Element => {
  const {
    imageArray = [],
    currentIndex = 0,
    updateSelectedImageIndex,
    isMobile = false,
    isRejected = false,
    reason = '',
    loop = true,
    backwardButtonClick,
    displayCountTag = false,
    productDetails = {},
    pageName = '',
    pageType = '',
    fromCustomerGridModal,
    isFleek = false
  } = props;
  const [imageIndex, setImageIndex] = useState(currentIndex);

  const { orderId = '', productBrick = '', productId = '', productName = '' } = productDetails;

  useEffect(() => {
    setImageIndex(props.currentIndex);
  }, [props.currentIndex]);

  const prevButtonHandler = () => {
    let newIndex = 0;
    if (loop) {
      newIndex = imageIndex > 0 ? imageIndex - 1 : imageArray.length - 1;
    } else {
      newIndex = imageIndex >= 0 ? imageIndex - 1 : imageArray.length - 1;
      backwardButtonClick && backwardButtonClick(true);
    }
    setImageIndex(newIndex);
    updateSelectedImageIndex && updateSelectedImageIndex(newIndex);
    gtmEventPush({
      event: 'reviews_rating_interactions',
      event_category: 'reviews_rating_interactions',
      event_action: 'photo interaction',
      event_label: 'left swipe',
      ...(orderId.length > 0 && { order_id: orderId }),
      ...(productId.length > 0 && { product_id: productId }),
      ...(productBrick.length > 0 && { product_brick: productBrick }),
      ...(productName.length > 0 && { product_name: productName }),
      page_name: pageName,
      page_type: pageType,
      store_type: 'ajio'
    });
  };
  const nextButtonHandler = () => {
    let newIndex = 0;
    if (loop) {
      newIndex = imageIndex === imageArray.length - 1 ? 0 : imageIndex + 1;
    } else {
      newIndex = imageIndex === imageArray.length ? 0 : imageIndex + 1;
      backwardButtonClick && backwardButtonClick(false);
    }
    setImageIndex(newIndex);
    updateSelectedImageIndex && updateSelectedImageIndex(newIndex);
    gtmEventPush({
      event: 'reviews_rating_interactions',
      event_category: 'reviews_rating_interactions',
      event_action: 'photo interaction',
      event_label: 'right swipe',
      ...(orderId.length > 0 && { order_id: orderId }),
      ...(productId.length > 0 && { product_id: productId }),
      ...(productBrick.length > 0 && { product_brick: productBrick }),
      ...(productName.length > 0 && { product_name: productName }),
      page_name: pageName,
      page_type: pageType,
      store_type: 'ajio'
    });
  };
  if (isMobile) {
    return fromCustomerGridModal ? (
      <div className={cx('image-slider-container-msite')}>
        <button
          className={cx('slider-button', 'left-button')}
          onClick={prevButtonHandler}
          aria-label="left arrow"
        >
          <img
            className="imageIcon"
            src={`${imageBaseRef}${
              isFleek ? 'fleek-left-arrow-reviews.svg' : 'left-arrow-msite.svg'
            }`}
            alt="Left arrow"
          />
        </button>
        {isRejected && <p className={cx('rejected-reason')}>Rejected Reason : {reason}</p>}
        <img
          className={cx('slider-image-review')}
          src={replaceWithJioCDN(imageArray[imageIndex])}
          alt={`image-index-${imageIndex}`}
        />
        <button
          className={cx('slider-button', 'right-button')}
          onClick={nextButtonHandler}
          aria-label="right arrow"
        >
          <img
            className="imageIcon"
            src={`${imageBaseRef}${
              isFleek ? 'fleek-right-arrow-reviews.svg' : 'right-arrow-msite.svg'
            }`}
            alt="Right arrow"
          />
        </button>
      </div>
    ) : (
      <div className={cx('image-slider-container-msite')}>
        {imageIndex !== 0 ? (
          <button
            className={cx('slider-button', 'left-button')}
            onClick={prevButtonHandler}
            aria-label="left arrow"
          >
            <img
              className="imageIcon"
              src={`${imageBaseRef}${
                isFleek ? 'fleek-left-arrow-reviews.svg' : 'left-arrow-msite.svg'
              }`}
              alt="Left arrow"
            />
          </button>
        ) : (
          <button className={cx('slider-button', 'left-button')} aria-label="left arrow">
            <span className="imageIcon" />
          </button>
        )}
        {isRejected && <p className={cx('rejected-reason')}>Rejected Reason : {reason}</p>}
        <img
          className={cx('slider-image-review')}
          src={replaceWithJioCDN(imageArray[imageIndex])}
          alt={`image-index-${imageIndex}`}
        />
        {imageIndex !== imageArray.length - 1 ? (
          <button
            className={cx('slider-button', 'right-button')}
            onClick={nextButtonHandler}
            aria-label="right arrow"
          >
            <img
              className="imageIcon"
              src={`${imageBaseRef}${
                isFleek ? 'fleek-right-arrow-reviews.svg' : 'right-arrow-msite.svg'
              }`}
              alt="Right arrow"
            />
          </button>
        ) : (
          <button className={cx('slider-button', 'right-button')} aria-label="right arrow">
            <span className="imageIcon" />
          </button>
        )}
      </div>
    );
  }
  return fromCustomerGridModal ? (
    <div className={cx('image-slider-container-desktop')}>
      <button className={cx('slider-button')} onClick={prevButtonHandler}>
        <img className="imageIcon" src={`${imageBaseRef}left-arrow-desktop.svg`} alt='Left arrow'/>
      </button>
      {isRejected && <p className={cx('rejected-reason')}>Rejected Reason : {reason}</p>}
      <img
        className={cx('slider-image-review')}
        src={replaceWithJioCDN(imageArray[imageIndex])}
        alt={`image-index-${imageIndex}`}
      />
      {displayCountTag && (
        <div className={cx('display-current-image-container')}>
          <p className={cx('display-current-image-count')}>
            {' '}
            {imageIndex + 1} of {imageArray.length}
          </p>
        </div>
      )}
      <button className={cx('slider-button')} onClick={nextButtonHandler}>
        <img className="imageIcon" src={`${imageBaseRef}right-arrow-desktop.svg`} alt='Right arrow'/>
      </button>
    </div>
  ) : (
    <div className={cx('image-slider-container-desktop')}>
      {imageIndex !== 0 ? (
        <button className={cx('slider-button')} onClick={prevButtonHandler}>
          <img className="imageIcon" src={`${imageBaseRef}left-arrow-desktop.svg`} alt='Left arrow'/>
        </button>
      ) : (
        <button className={cx('slider-button')}>
          <span className="imageIcon" />
        </button>
      )}
      {isRejected && <p className={cx('rejected-reason')}>Rejected Reason : {reason}</p>}
      <img
        className={cx('slider-image-review')}
        src={replaceWithJioCDN(imageArray[imageIndex])}
        alt={`image-index-${imageIndex}`}
      />
      {displayCountTag && (
        <div className={cx('display-current-image-container')}>
          <p className={cx('display-current-image-count')}>
            {' '}
            {imageIndex + 1} of {imageArray.length}
          </p>
        </div>
      )}
      {imageIndex !== imageArray.length - 1 ? (
        <button className={cx('slider-button')} onClick={nextButtonHandler}>
          <img className="imageIcon" src={`${imageBaseRef}right-arrow-desktop.svg`} alt='Right arrow'/>
        </button>
      ) : (
        <button className={cx('slider-button')}>
          <span className="imageIcon" />
        </button>
      )}
    </div>
  );
};

export default ImageSlider;
